import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SiGithub, SiLinkedin } from "react-icons/si";
import { BiSolidEnvelope, BiMenu, BiX } from "react-icons/bi";

interface NavLink {
  label: string;
  href: string;
  isRoute?: boolean; // true = react-router Link, false = anchor scroll
}

const NAV_LINKS: NavLink[] = [
  { label: "Sobre mí", href: "/#about" },
  { label: "Proyectos", href: "/#projects" },
  { label: "Blog", href: "/blog" },
  { label: "Contacto", href: "/contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const HEADER_HEIGHT = 80; // px offset to avoid section title hidden under header

  const scrollToHash = (hash: string) => {
    const el = document.querySelector(hash);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - HEADER_HEIGHT;
    window.scrollTo({ top, behavior: "smooth" });
  };

  const handleNavClick = (href: string) => {
    setMenuOpen(false);

    // Logo — just go home
    if (href === "/") {
      navigate("/");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const hash = href.startsWith("/#") ? href.slice(1) : null;

    if (!hash) {
      navigate(href);
      return;
    }

    if (isHome) {
      scrollToHash(hash);
    } else {
      navigate("/");
      setTimeout(() => scrollToHash(hash), 150);
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-in-out border-b ${scrolled || menuOpen
          ? "bg-gray-900/95 backdrop-blur-md border-gray-400/40 shadow-lg shadow-black/20"
          : "bg-transparent border-transparent"
          }`}
      >
        <div className="max-w-7xl mx-auto px-8 sm:px-16 flex items-center h-16 gap-6">
          {/* Logo */}
          <button
            onClick={() => handleNavClick("/")}
            className="flex items-center gap-2 group mr-auto"
          >
            <span className="w-2 h-2 rounded-full bg-green-300 group-hover:scale-125 transition-transform duration-300" />
            <span className="font-header font-bold text-white text-sm tracking-wide group-hover:text-green-300 transition-colors duration-300">
              Franco Martino
            </span>
          </button>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.href)}
                className="px-3 py-1.5 text-sm text-gray-400 hover:text-white rounded-lg hover:bg-gray-800/60 transition-all duration-200"
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Social icons — desktop */}
          <div className="hidden md:flex items-center gap-3 text-gray-400 ml-2">
            <a
              href="https://github.com/FrancoMartino/"
              target="_blank"
              rel="noreferrer"
              className="hover:text-white transition-colors duration-200"
              aria-label="GitHub"
            >
              <SiGithub size={18} />
            </a>
            <a
              href="https://www.linkedin.com/in/francomartino/"
              target="_blank"
              rel="noreferrer"
              className="hover:text-white transition-colors duration-200"
              aria-label="LinkedIn"
            >
              <SiLinkedin size={18} />
            </a>
            <a
              href="mailto:martinofrancoezequiel@gmail.com"
              className="hover:text-white transition-colors duration-200"
              aria-label="Email"
            >
              <BiSolidEnvelope size={20} />
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="md:hidden text-gray-400 hover:text-white transition-colors duration-200 p-1"
            aria-label="Menú"
          >
            {menuOpen ? <BiX size={24} /> : <BiMenu size={24} />}
          </button>
        </div>

        {/* Mobile menu */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ${menuOpen ? "max-h-96 pb-4" : "max-h-0"
            }`}
        >
          <nav className="flex flex-col px-8 gap-1">
            {NAV_LINKS.map((link) => (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.href)}
                className="text-left px-3 py-3 text-sm text-gray-300 hover:text-white hover:bg-gray-800/60 rounded-lg transition-all duration-200"
              >
                {link.label}
              </button>
            ))}
            {/* Social mobile */}
            <div className="flex gap-4 items-center px-3 pt-3 mt-1 border-t border-gray-800/60 text-gray-400">
              <a
                href="https://github.com/FrancoMartino/"
                target="_blank"
                rel="noreferrer"
                className="hover:text-white transition-colors duration-200"
                aria-label="GitHub"
              >
                <SiGithub size={20} />
              </a>
              <a
                href="https://www.linkedin.com/in/francomartino/"
                target="_blank"
                rel="noreferrer"
                className="hover:text-white transition-colors duration-200"
                aria-label="LinkedIn"
              >
                <SiLinkedin size={20} />
              </a>
              <a
                href="mailto:martinofrancoezequiel@gmail.com"
                className="hover:text-white transition-colors duration-200"
                aria-label="Email"
              >
                <BiSolidEnvelope size={22} />
              </a>
            </div>
          </nav>
        </div>
      </header>

      {/* Spacer so content doesn't hide under fixed header */}
      <div className="h-16" />
    </>
  );
}
